/**
 * 爱上东海 · 分享链接站点 —— 唯一配置源。
 *
 * 为什么要集中在一个文件里：
 *   有三处地方必须描述「同一件事」——同一个域名、同一个包名、同一批商店：
 *     1. public/.well-known/assetlinks.json            （Android App Links 绑定）
 *     2. public/.well-known/apple-app-site-association （iOS Universal Links 绑定）
 *     3. 前端页面上的下载入口与深链 scheme
 *   分散着写，迟早会出现「改了绑定文件忘了改页面」这种没有任何报错的漂移：
 *   系统认了域名但页面拼错链接，或者反过来。这里定义一次，
 *   生成脚本（scripts/generate-association.mjs）和前端（src/config.js）
 *   都从这同一个文件读。
 *
 * 带 TODO 的字段是还没拿到的真实值，上线前必须补齐，
 * 补完后执行 `npm run gen:assoc` 重新生成绑定文件。
 */

/** 站点自身信息：绝对链接、绑定文件里的域名都来自这里。 */
export const site = {
  /**
   * 站点域名（只有协议 + 主机，不带结尾斜杠、不带路径）。
   *
   * ！！这个值必须和 App 端一致：
   *   - iOS  app.json → ios.associatedDomains: ["applinks:api.samecity.aidonghai.cn"]
   *   - Android app.json → android.intentFilters 里的 host
   * 三处不一致时，系统不会唤起 App，而且不会有任何提示。
   * 改完必须重新 `npm run gen:assoc`，否则绑定文件里还是旧域名。
   */
  url: 'https://share.samecity.aidonghai.cn',

  /**
   * 站点在域名下的子路径。独占一个子域名，所以是 ''（域名的根）。
   *
   * 为什么用独立子域名而不是 api 域名下的 /web 子目录：
   * Universal Link 要求分享链接的 path 直接等于 App 路由（/posts/1），
   * 而 AASA / assetlinks 又必须能在**域名根**访问。子域名同时满足两条：
   *   - 站点独占 share.samecity.aidonghai.cn 的根，/posts/1 天然落到本站
   *   - /.well-known/ 就在 dist 里，不需要 alias，也不需要 302 转发
   *   - api.samecity.aidonghai.cn 的 root 完全不受影响
   * 代价只有 DNS 记录 + 证书。
   *
   * 如果要换名字，只改上面 url 这一行 + 这里不用动，然后：
   *   npm run gen:assoc && npm run build
   */
  basePath: '',

  /** 站点名称，用于 <title>、分享卡片兜底标题。 */
  name: '爱上东海',

  /** 分享卡片兜底描述。 */
  description: '同城生活，就在爱上东海。附近的人和事、圈子、商城、聊天，一个 App 搞定。',
};

/**
 * App 的身份信息。
 * 包名 / Bundle ID / scheme 全部取自 App 仓库的 app.json，不要手改猜测。
 */
export const app = {
  name: '爱上东海',
  slug: 'ai-shang-dong-hai',
  /** 自定义 URL Scheme。来自 app.json 的 expo.scheme。 */
  scheme: 'aishangdonghai',
  version: '1.0.0',
  slogan: '同城生活，就在爱上东海',

  ios: {
    /** 来自 app.json 的 expo.ios.bundleIdentifier */
    bundleId: 'com.suiruanaishangdonghai.samecity',
    /**
     * TODO(必填)：Apple Developer 的 Team ID（10 位，如 A1B2C3D4E5）。
     * apple-app-site-association 里的 appID 是 `<TeamID>.<BundleID>`，
     * 缺了它就只是占位文件，Universal Links 不会生效。
     * 在 developer.apple.com → Membership 里能看到。
     */
    teamId: '3CMC4942FM',
    /**
     * TODO(上架后填)：App Store 数字 ID（App Store 链接里 /id 后面那串数字）。
     * 留空时，下载按钮会退化成「搜索应用名称」的 App Store 搜索页。
     */
    appStoreId: '',
  },

  android: {
    /** 来自 app.json 的 expo.android.package */
    packageName: 'com.suiruanaishangdonghai.samecity',
    /**
     * 签名证书的 SHA-256 指纹（冒号大写十六进制）。
     * assetlinks.json 靠它校验「这个包确实是我签的」，缺了 App Links 不生效。
     *
     * 这里是 release.keystore 的指纹。取值命令（在 App 仓库根目录执行，密码问运维）：
     *   keytool -list -v -keystore credentials/release.keystore -alias <别名>
     *
     * 如果同时还用 EAS 构建并分发，EAS 用的是另一套签名证书，
     * 要把 EAS 那份指纹也追加进这个数组（数组支持多份证书）。
     * 顺序无所谓，随便写大写/小写/带空格，生成脚本会统一成标准格式。
     */
    sha256CertFingerprints: [
      'D4:1A:36:D4:C6:F0:2B:A0:8B:F8:6C:D6:C2:E1:D8:85:D4:4D:D8:A6:70:62:EB:C1:4B:4A:88:68:0D:CC:55:BD',
    ],
  },
};

/**
 * 分享链接的行为配置。
 */
export const shareLink = {
  /**
   * 分享链接的主形态：**直接就是 App 路由**。
   *
   *   https://share.samecity.aidonghai.cn/posts/1   →   App 内 /posts/1
   *
   * 为什么必须这样：Universal Link / App Links 是把 URL 的 path **原样**交给 App。
   * App 的路由是 /posts/1，所以分享链接也必须是 /posts/1 —— 如果换成
   * 站点自己的 /s/<token> 形态，App 收到后匹配不到任何路由，等于没打开
   * （除非在 App 侧加 +native-intent 做改写）。这条路 App 侧零改动。
   *
   * 下面的 allowedPathPrefixes 就是「哪些 App 路由可以分享」，它同时决定：
   *   1. 深链白名单（防开放跳转，见 share-token.js）
   *   2. apple-app-site-association 里声明哪些路径归 App
   *   3. Android intentFilters 里的 pathPrefix（App 端要手动对齐）
   *
   * ⚠️ 每一项都必须是 App **真实存在的顶层路由**，必须和 App 端一致。
   *    多写一项比漏写严重得多：AASA 会声明一个 App 到不了的路径，
   *    装了 App 的人点开直接落 not-found，比不唤起还糟，且只在线上暴露。
   *    `npm test` 会直接读旁边 App 仓库的 src/app 核对，漂移当场失败。
   *
   * 注意 topics 不在列表里：话题页真实路由是 /posts/topic（已被 posts 覆盖），
   * 不存在顶层 /topics。这里曾经误加过，就是被上面那条检查抓出来的。
   */
  allowedPathPrefixes: [
    'posts', // 帖子详情（含 /posts/topic、/posts/by-type）
    'users', // 用户主页
    'shops', // 商家主页
    'circles', // 圈子
    'chat', // 会话
    'message', // 消息 / 通知
    'my', // 我的
    'search', // 搜索结果
    'ai-chat', // AI 助手
  ],

  /** 唤起 App 后等待多久还停留在浏览器，就判定为「未安装」。 */
  openTimeoutMs: 2500,
};

/**
 * 微信登录回跳（iOS Universal Link）。
 *
 * 微信 OpenSDK 1.8.6+ 起，从微信回到第三方 App 走的是 Universal Link：微信会在
 * 这个地址后面拼上路径和参数（形如 https://<域名>/wxauth/<随机串>?code=…）。
 * 也就是说这条路径必须和分享链接**同一个域名**，并且出现在 AASA 里，否则系统
 * 不会唤起 App —— 微信侧会退化成「二次确认弹窗」，登录流程直接断在那里。
 *
 * 同一个值要出现在三处，少一处都只在真机上表现为「回不到 App」：
 *   1. 这里：AASA 由 npm run gen:assoc 从这里生成 /wxauth/*
 *   2. App 仓库 .env 的 EXPO_PUBLIC_WECHAT_UNIVERSAL_LINK
 *      （编译进 iOS associatedDomains 和 registerApp:universalLink:）
 *   3. 微信开放平台后台 → 移动应用 → 开发信息 → iOS → Universal Links
 *
 * 要求：https、以 / 结尾、不带 query。
 */
export const wechat = {
  universalLink: 'https://share.samecity.aidonghai.cn/wxauth/',
};

/**
 * 站点自己的前端路由（不是 App 路由，只属于这个 Web 站点）：
 *
 *   /            下载引导页
 *   /s/:token    分享落地页（可选形态，链接里能带卡片标题/描述，
 *                打开后由本站解出 App 路径再用 scheme 唤起）
 *   *            其余一律当分享链接处理（就是 /posts/1 这种）
 *
 * 这些路径**故意不写进 apple-app-site-association**：App 里没有对应路由，
 * 声明了会让已安装的设备点开后落到 not-found。
 * 具体见 scripts/generate-association.mjs 的 collectAssociationPaths。
 */

/**
 * 各手机厂商应用商店的下载入口。
 *
 * 当前 App 还没上架，所以这里用的是「按包名直达」的商店协议：
 * `appmarket://details?id=<包名>` 这类链接不依赖商店内部 ID，
 * 上架后同一个链接就能定位到详情页，不需要改代码。
 *
 * 上架后要做的事：把 web 字段换成商店详情页直达链接（见 README「上架后要改什么」）。
 *
 * 字段说明：
 *   scheme  用本机商店 App 打开详情页（仅在该品牌手机上有效）
 *   web     浏览器可打开的网页版（没配就表示该商店没有稳定的网页版）
 *   ua      识别该品牌手机的 UA 片段，用于把「推荐」排到第一位
 */
export const stores = [
  {
    id: 'ios',
    type: 'ios',
    name: 'App Store',
    vendor: 'iPhone / iPad',
    ua: /iPhone|iPad|iPod/i,
    scheme: 'itms-apps://apps.apple.com/cn/app/id{appStoreId}',
    web: 'https://apps.apple.com/cn/app/id{appStoreId}',
  },
  {
    id: 'huawei',
    type: 'android',
    name: '华为应用市场',
    vendor: '华为',
    ua: /HUAWEI|HarmonyOS/i,
    scheme: 'appmarket://details?id={package}',
    web: 'https://appgallery.huawei.com/',
  },
  {
    id: 'honor',
    type: 'android',
    name: '荣耀应用市场',
    vendor: '荣耀',
    // 荣耀独立后是自家商店，但 appmarket:// 协议仍沿用；若某机型失效，
    // 把 web 换成荣耀商店详情页即可，前端不用动。
    ua: /HONOR/i,
    scheme: 'appmarket://details?id={package}',
    web: 'https://appgallery.huawei.com/',
  },
  {
    id: 'xiaomi',
    type: 'android',
    name: '小米应用商店',
    vendor: '小米 / 红米',
    ua: /Xiaomi|Redmi|POCO|\bMI\s/i,
    scheme: 'mimarket://details?id={package}',
    web: 'https://app.xiaomi.com/details?id={package}',
  },
  {
    id: 'oppo',
    type: 'android',
    name: 'OPPO 软件商店',
    vendor: 'OPPO / 一加 / realme',
    ua: /OPPO|CPH\d{4}|OnePlus|realme/i,
    scheme: 'oppomarket://details?packagename={package}',
    web: '',
  },
  {
    id: 'vivo',
    type: 'android',
    name: 'vivo 应用商店',
    vendor: 'vivo / iQOO',
    ua: /vivo|iQOO|V\d{4}[A-Z]?/i,
    scheme: 'vivomarket://details?id={package}',
    web: '',
  },
  {
    id: 'samsung',
    type: 'android',
    name: '三星应用商店',
    vendor: '三星',
    ua: /Samsung|SM-[A-Z]\d/i,
    scheme: 'samsungapps://ProductDetail/{package}',
    web: '',
  },
  {
    id: 'meizu',
    type: 'android',
    name: '魅族应用商店',
    vendor: '魅族',
    ua: /MEIZU/i,
    scheme: 'mstore://details?package_name={package}',
    web: 'https://app.meizu.com/apps/public/detail?package_name={package}',
  },
  {
    id: 'tencent',
    type: 'android',
    name: '应用宝',
    vendor: '通用 / 微信内可用',
    ua: null,
    // 应用宝没有稳定的自定义协议，直接走网页版；微信内置浏览器也放行。
    scheme: '',
    web: 'https://a.app.qq.com/o/simple.jsp?pkgname={package}',
  },
  {
    id: 'google',
    type: 'android',
    name: 'Google Play',
    vendor: '海外安卓',
    ua: null,
    scheme: 'market://details?id={package}',
    web: 'https://play.google.com/store/apps/details?id={package}',
  },
];

/**
 * 未上架阶段的兜底：如果 App 官网/H5 下载页已经有了，把地址填在这里，
 * 首页会把它作为安卓的「主按钮」，比跳到还没上架的应用商店更靠谱。
 * 留空则不显示。
 */
export const officialDownloadUrl = '';
